import type { IconSourceData } from '@lumen5/luminary';
import type Luminary from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { BaseRegistry, type ItemRepresentation } from './BaseRegistry';

import type { AssetContainerModel, MediaCandidates } from 'js/src/client';

interface IconRepresentation extends ItemRepresentation {
    type: 'icon';
}

export class IconRegistry extends BaseRegistry<IconRepresentation> {
    private static instance: IconRegistry;

    public static getInstance(): IconRegistry {
        if (!IconRegistry.instance) {
            IconRegistry.instance = new IconRegistry();
        }
        return IconRegistry.instance;
    }

    protected validateItemData(data: AssetContainerModel): void {
        if (!data.icon) {
            throw new Error('Invalid icon data: must contain icon property');
        }
    }

    protected extractItemInfo(data: AssetContainerModel): {
        title: string;
        description: string;
        tags: string[];
    } {
        const title = data.icon?.title || 'Untitled';
        const description = data.icon?.description || '';
        const tags = Array.isArray(data.icon?.tags) ? data.icon.tags : [];

        return { title, description, tags };
    }

    protected createRepresentation(
        id: string,
        itemInfo: { title: string; description: string; tags: string[] },
        data: AssetContainerModel,
    ): IconRepresentation {
        return {
            id,
            type: 'icon',
            title: itemInfo.title,
            description: itemInfo.description,
            tags: itemInfo.tags,
        };
    }

    protected getItemUuid(data: AssetContainerModel): string | undefined {
        return data.icon?.uuid ?? undefined;
    }

    protected isItemAlreadyInRegistry(uuid: string): string | null {
        for (const id of this.registry.keys()) {
            const entry = this.registry.get(id);
            if (!entry) {
                continue;
            }
            if (entry.data.icon?.uuid === uuid) {
                return id;
            }
        }
        return null;
    }

    public getIconSourceData(id: string): IconSourceData | null {
        const entry = this.registry.get(id);
        if (!entry) {
            return null;
        }

        if (entry.data.icon) {
            return entry.data.icon as IconSourceData;
        }

        return null;
    }

    public async addAnyUnseenItemsFromVideo(luminary: Luminary): Promise<void> {
        // Go through all scenes and extract icon assets
        const unseenUuids = new Set<string>();
        for (const scene of luminary.scenes) {
            const iconAssets = scene.getAssetsByType(VideoData.ICON_ASSET_TYPE);
            for (const asset of iconAssets) {
                const uuid = asset.sourceData.uuid;
                if (uuid && !this.getItemIdByUuid(uuid)) {
                    unseenUuids.add(uuid);
                }
            }
        }

        const assets = await this.fetchItemsFromApi(unseenUuids);

        for (const asset of assets) {
            if (asset.icon) {
                this.addItem(asset);
            }
        }
    }

    /**
     * Prepopulates the IconRegistry with icon candidates from video recommendations.
     */
    public async addCandidatesFromRecommendations(
        mediaCandidates: MediaCandidates,
    ): Promise<number> {
        const allUuids = new Set<string>();

        // Collect all UUIDs from different media candidate arrays
        if (mediaCandidates.stock_icons) {
            mediaCandidates.stock_icons.forEach((uuid) => allUuids.add(uuid));
        }
        if (mediaCandidates.uploaded_icons) {
            mediaCandidates.uploaded_icons.forEach((uuid) => allUuids.add(uuid));
        }

        // Filter out UUIDs that are already in the registry
        const unseenUuids = new Set<string>();
        for (const uuid of allUuids) {
            if (!this.getItemIdByUuid(uuid)) {
                unseenUuids.add(uuid);
            }
        }

        if (unseenUuids.size === 0) {
            return 0;
        }

        // Fetch asset data from API (will now include AI descriptions for source media)
        const assets = await this.fetchItemsFromApi(unseenUuids);

        // Add each asset to the registry
        let addedCount = 0;
        for (const asset of assets) {
            if (asset.background) {
                this.addItem(asset);
                addedCount++;
            }
        }

        return addedCount;
    }
}

export type SerializedIconRegistry = ReturnType<IconRegistry['serialize']>;
